import React from 'react';
import countries from 'i18n-iso-countries';
import {
  Box,
  Paper,
  Theme,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { Institute } from '../../redux/types';

const LabelText = styled(Typography)(({ theme }: { theme: Theme }) => ({
  width: 325,
  color: theme.palette.text.secondary,
}));

const ValueText = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.text.primary,
}));

type Props = {
  institute: Institute;
  locale: string;
}

const InstituteFormGroup: React.FC<Props> = ({
  institute,
  locale,
}: Props) => {
  if (!institute) {
    return (
      <Box className="bg-light-gray flex items-center justify-center mb3" sx={{ minHeight: '10em', width: '100%' }}>
        <FormattedMessage id="instituteView.noInstituteAssigned" />
      </Box>
    );
  }
  const hasCertificateAddress = !!institute.certificationName;
  const hasInvoiceAddress = !!institute.invoiceName;
  return (
    <Box sx={{ my: 2 }} id="container" display="flex" flexWrap="wrap" flexDirection="column">

      <Paper id="institute-details-container" sx={{ p: 3 }}>
        <Box sx={{ m: 1 }}>
          <Typography variant="h5">
            <FormattedMessage id="account.instituteAddress" />
          </Typography>
        </Box>
        <Box sx={{ m: 1 }}>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.hospitalName" />
            </LabelText>
            <ValueText>
              {institute.hospitalName}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.instituteName" />
            </LabelText>
            <ValueText>
              {institute.name}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.instituteStreet" />
            </LabelText>
            <ValueText>
              {institute.street}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.instituteHousenumber" />
            </LabelText>
            <ValueText>
              {institute.housenumber}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.instituteAdditionalAddress" />
            </LabelText>
            <ValueText>
              {institute.additionalAddress}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.instituteZip" />
            </LabelText>
            <ValueText>
              {institute.zip}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.instituteCity" />
            </LabelText>
            <ValueText>
              {institute.city}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.instituteCountry" />
            </LabelText>
            <ValueText>
              {countries.getName(institute.country, locale)}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.institutePhone" />
            </LabelText>
            <ValueText>
              {institute.phone}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.instituteFax" />
            </LabelText>
            <ValueText>
              {institute.fax}
            </ValueText>
          </Box>
          <Box display="flex">
            <LabelText>
              <FormattedMessage id="registration.instituteEmail" />
            </LabelText>
            <ValueText>
              {institute.email}
            </ValueText>
          </Box>
        </Box>
      </Paper>

      {
        hasCertificateAddress && (
          <Paper id="certificate-details-container" sx={{ p: 3 }}>
            <Box sx={{ m: 1 }}>
              <Typography variant="h5">
                <FormattedMessage id="account.certificateAddress" />
              </Typography>
            </Box>
            <Box sx={{ m: 1 }}>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceName" />
                </LabelText>
                <ValueText>
                  {institute.certificationName}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceStreet" />
                </LabelText>
                <ValueText>
                  {institute.certificationStreet}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceHousenumber" />
                </LabelText>
                <ValueText>
                  {institute.certificationHousenumber}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceZip" />
                </LabelText>
                <ValueText>
                  {institute.certificationZip}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceCity" />
                </LabelText>
                <ValueText>
                  {institute.certificationCity}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceCountry" />
                </LabelText>
                <ValueText>
                  {countries.getName(institute.country, locale)}
                </ValueText>
              </Box>
            </Box>
          </Paper>
        )
      }

      {
        hasInvoiceAddress && (

          <Paper id="invoice-details-container" sx={{ p: 3 }}>
            <Box sx={{ m: 1 }}>
              <Typography variant="h5">
                <FormattedMessage id="account.invoiceAddress" />
              </Typography>
            </Box>
            <Box sx={{ m: 1 }}>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceName" />
                </LabelText>
                <ValueText>
                  {institute.invoiceName}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceStreet" />
                </LabelText>
                <ValueText>
                  {institute.invoiceStreet}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceHousenumber" />
                </LabelText>
                <ValueText>
                  {institute.invoiceHousenumber}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceZip" />
                </LabelText>
                <ValueText>
                  {institute.invoiceZip}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceCity" />
                </LabelText>
                <ValueText>
                  {institute.invoiceCity}
                </ValueText>
              </Box>
              <Box display="flex">
                <LabelText>
                  <FormattedMessage id="registration.invoiceCountry" />
                </LabelText>
                <ValueText>
                  {countries.getName(institute.country, locale)}
                </ValueText>
              </Box>
            </Box>
          </Paper>
        )
      }

    </Box>
  );
};

export default InstituteFormGroup;
